import SEO from "../../components/SEO";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BuildingOffice2Icon,
  AcademicCapIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
  BoltIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  LockClosedIcon,
  IdentificationIcon,
  AtSymbolIcon,
  EnvelopeIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";

import bannerSchool from "../../assets/banner-school.png";

/* ── Data ── */
const institutions = [
  {
    value: 1,
    Icon: AcademicCapIcon,
    title: "Instituição Superior",
    subtitle: "Universidades · Politécnicos · Institutos",
    desc: "Receba propinas em tempo real. Convocatórias, chat e e-mail integrados.",
  },
  {
    value: 2,
    Icon: BuildingOffice2Icon,
    title: "Ensino Secundário",
    subtitle: "Colégios · Escolas · Pré-escolar",
    desc: "Os encarregados pagam pelo telemóvel ou PC, sem filas nem deslocações.",
  },
];

const perks = [
  { Icon: ShieldCheckIcon,       label: "Pagamentos 100% seguros" },
  { Icon: BoltIcon,              label: "Propinas recebidas em tempo real" },
  { Icon: DevicePhoneMobileIcon, label: "Web, Mobile e TV" },
];

const STEPS = [
  { number: 1, label: "Tipo" },
  { number: 2, label: "Código" },
  { number: 3, label: "Dados" },
  { number: 4, label: "Verificação" },
];

/* ── Input helper ── */
function Field({
  Icon, label, type = "text", placeholder,
}: { Icon: React.ElementType; label: string; type?: string; placeholder: string }) {
  return (
    <div>
      <label className="block text-dark/70 text-xs font-semibold mb-1.5">{label}</label>
      <div className="flex items-center gap-3 border-2 border-border rounded-xl px-4 py-3 bg-white focus-within:border-blue transition-colors">
        <Icon className="w-4 h-4 text-muted shrink-0" />
        <input
          type={type}
          placeholder={placeholder}
          className="flex-1 text-sm text-dark placeholder:text-muted/60 outline-none bg-transparent"
        />
      </div>
    </div>
  );
}

/* ── OTP boxes ── */
function OtpInput() {
  return (
    <div className="flex gap-3 justify-center">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          type="text"
          maxLength={1}
          className="w-11 h-13 text-center text-lg font-bold text-dark border-2 border-border rounded-xl focus:border-blue outline-none transition-colors bg-white"
        />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
export default function Register() {
  const [step, setStep]     = useState(1);
  const [idUser, setIdUser] = useState(0);
  const navigate            = useNavigate();

  const next = () => {
    if (step < 4) setStep(step + 1);
    else navigate("/");
  };
  const back = () => { if (step > 1) setStep(step - 1); };

  const canAdvance =
    (step === 1 && idUser !== 0) ||
    step === 2 ||
    step === 3 ||
    step === 4;

  /* ── Step content ── */
  const stepContent: Record<number, { title: string; subtitle: string; body: React.ReactNode }> = {
    1: {
      title: "Tipo de instituição",
      subtitle: "Seleccione o tipo que melhor descreve a sua instituição",
      body: (
        <div className="flex flex-col gap-4">
          {institutions.map(({ value, Icon, title, subtitle, desc }) => {
            const sel = idUser === value;
            return (
              <label
                key={value}
                className={`flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${
                  sel
                    ? "border-blue bg-blue/5 shadow-md shadow-blue/10"
                    : "border-border bg-white hover:border-blue/40 hover:shadow-sm"
                }`}
              >
                <input
                  type="radio"
                  name="usuario"
                  value={value}
                  onChange={() => setIdUser(value)}
                  className="sr-only"
                />
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-all"
                  style={sel ? { background: "linear-gradient(135deg, #2babe3, #016fb9)" } : { background: "#ebf5fb" }}
                >
                  <Icon className={`w-5 h-5 ${sel ? "text-white" : "text-blue"}`} />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className={`font-bold text-sm mb-0.5 ${sel ? "text-blue" : "text-dark"}`}>{title}</p>
                  <p className={`text-[11px] font-semibold mb-1.5 ${sel ? "text-blue/60" : "text-muted"}`}>{subtitle}</p>
                  <p className={`text-xs leading-relaxed ${sel ? "text-dark/65" : "text-muted"}`}>{desc}</p>
                </div>
                <div
                  className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all"
                  style={sel ? { background: "linear-gradient(135deg, #2babe3, #016fb9)", borderColor: "transparent" } : { borderColor: "#cbe4f5" }}
                >
                  {sel && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
              </label>
            );
          })}
        </div>
      ),
    },

    2: {
      title: "Código de adesão",
      subtitle: "Introduza o código de adesão fornecido pela PIA",
      body: (
        <div className="flex flex-col gap-5">
          <div className="bg-sky border border-border rounded-2xl p-4 flex gap-3">
            <LockClosedIcon className="w-5 h-5 text-blue shrink-0 mt-0.5" />
            <p className="text-dark/70 text-xs leading-relaxed">
              Para iniciar o processo de abertura de conta, precisamos de validar
              o seu código de adesão. Se não tiver um código, contacte-nos.
            </p>
          </div>
          <Field Icon={KeyIcon} label="Código de adesão" placeholder="Ex: PIA-2024-XXXX" />
        </div>
      ),
    },

    3: {
      title: "Informações de acesso",
      subtitle: "Dados do administrador da instituição",
      body: (
        <div className="flex flex-col gap-4">
          <Field Icon={IdentificationIcon} label="Nome do representante" placeholder="Ex: João Silva" />
          <Field Icon={AtSymbolIcon}       label="E-mail institucional"   placeholder="admin@instituicao.ao" type="email" />
          <Field Icon={LockClosedIcon}     label="Palavra-passe"          placeholder="Mínimo 8 caracteres" type="password" />
          <div className="flex items-start gap-2 mt-1">
            <input type="checkbox" id="terms" className="mt-0.5 accent-blue" />
            <label htmlFor="terms" className="text-xs text-muted leading-relaxed">
              Aceito os{" "}
              <a href="/terms" className="text-blue font-semibold hover:underline">Termos e Condições</a>
              {" "}e a{" "}
              <a href="/privacy" className="text-blue font-semibold hover:underline">Política de Privacidade</a>
            </label>
          </div>
        </div>
      ),
    },

    4: {
      title: "Verificação de e-mail",
      subtitle: "Introduza o código de 6 dígitos enviado para o seu e-mail",
      body: (
        <div className="flex flex-col gap-6">
          <div className="bg-sky border border-border rounded-2xl p-4 flex gap-3">
            <EnvelopeIcon className="w-5 h-5 text-blue shrink-0 mt-0.5" />
            <p className="text-dark/70 text-xs leading-relaxed">
              Enviámos um código de verificação para o seu e-mail.
              Verifique a caixa de entrada e o spam.
            </p>
          </div>
          <OtpInput />
          <p className="text-center text-muted text-xs">
            Não recebeu?{" "}
            <button className="text-blue font-semibold hover:underline">Reenviar código</button>
          </p>
        </div>
      ),
    },
  };

  const { title, subtitle, body } = stepContent[step];

  return (
    <div className="min-h-screen flex max-md:flex-col">
      <SEO
        title="Registar Instituição"
        description="Registe a sua instituição de ensino na plataforma PIA em menos de 5 minutos. Activação em 24h, gratuito, sem compromisso. A plataforma de gestão escolar líder em Angola."
        path="/register"
      />

      {/* ══════════════════════════════
          LEFT — photo panel (fixo)
      ══════════════════════════════ */}
      <div
        className="relative flex-1 flex flex-col justify-between px-14 py-12 overflow-hidden max-md:py-10 max-md:px-8"
        style={{
          backgroundImage: `url(${bannerSchool})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
        }}
      >
        {/* Dark gradient overlay */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(160deg, rgba(0,22,39,0.92) 0%, rgba(0,40,73,0.82) 45%, rgba(5,105,163,0.70) 100%)" }}
        />
        {/* Bottom fade para legibilidade */}
        <div
          className="absolute bottom-0 left-0 w-full h-48 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(0,22,39,0.85), transparent)" }}
        />
        {/* Centre */}
        <div className="relative flex-1 flex flex-col justify-center py-14 max-w-sm">
          <span className="text-blue-light text-xs font-semibold uppercase tracking-widest mb-4">
            Plataforma de pagamentos escolares
          </span>
          <h1 className="text-4xl font-extrabold text-white leading-[1.15] mb-5">
            Modernize a gestão da sua{" "}
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #2babe3, #7dd3fc)" }}
            >
              instituição
            </span>
          </h1>
          <p className="text-white/50 text-sm leading-relaxed mb-10">
            Receba propinas, envie convocatórias e gerencie a sua escola num só lugar.
          </p>
          <div className="flex flex-col gap-4">
            {perks.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/8 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-blue-light" />
                </div>
                <span className="text-white/65 text-sm">{label}</span>
              </div>
            ))}
          </div>

          {/* Stats card */}
          <div className="mt-12 flex items-center gap-4 bg-white/8 backdrop-blur-sm border border-white/12 rounded-2xl px-5 py-4">
            {[
              { val: "500+", lbl: "Instituições" },
              { val: "50k+", lbl: "Estudantes" },
            ].map(({ val, lbl }, i) => (
              <div key={lbl} className="flex items-center gap-4">
                {i > 0 && <div className="w-px h-8 bg-white/15" />}
                <div>
                  <p className="text-white font-extrabold text-xl leading-none">{val}</p>
                  <p className="text-white/40 text-xs mt-1">{lbl}</p>
                </div>
              </div>
            ))}
            <div className="w-px h-8 bg-white/15" />
            <div className="flex flex-col items-center">
              <CheckCircleIcon className="w-5 h-5 text-emerald-400" />
              <p className="text-white/40 text-xs mt-1">Verificado</p>
            </div>
          </div>
        </div>

        <p className="relative text-white/25 text-xs">© 2024 PIA · Todos os direitos reservados</p>
      </div>

      {/* Divider */}
      <div className="w-px bg-border max-md:hidden" />

      {/* ══════════════════════════════
          RIGHT — form panel (muda)
      ══════════════════════════════ */}
      <div className="flex-1 bg-off-white flex flex-col justify-center px-14 py-16 max-md:px-8">

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-10">
          {STEPS.map(({ number, label }, i) => {
            const done    = number < step;
            const current = number === step;
            return (
              <div key={number} className="flex items-center gap-1">
                <div className="flex flex-col items-center gap-1">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300"
                    style={
                      done
                        ? { background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff" }
                        : current
                        ? { background: "linear-gradient(135deg, #2babe3, #016fb9)", color: "#fff" }
                        : { background: "#e2eaf2", color: "#9fb8cc" }
                    }
                  >
                    {done ? <CheckCircleIcon className="w-4 h-4" /> : number}
                  </div>
                  <span
                    className={`text-[10px] font-semibold ${
                      current ? "text-blue" : done ? "text-emerald-500" : "text-muted/60"
                    }`}
                  >
                    {label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className="h-0.5 w-8 rounded-full mb-4 transition-all duration-300"
                    style={{ background: done ? "#10b981" : "#e2eaf2" }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Step heading */}
        <h2 className="text-2xl font-extrabold text-dark mb-1">{title}</h2>
        <p className="text-muted text-sm mb-7">{subtitle}</p>

        {/* Step body */}
        <div className="mb-8">{body}</div>

        {/* Navigation buttons */}
        <div className="flex gap-3">
          {step > 1 && (
            <button
              onClick={back}
              className="flex items-center gap-2 px-6 py-4 rounded-2xl border-2 border-border text-dark font-semibold text-sm hover:border-blue hover:text-blue transition-all"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Voltar
            </button>
          )}
          <button
            onClick={next}
            disabled={!canAdvance}
            className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            style={canAdvance ? { background: "linear-gradient(90deg, #2babe3, #016fb9)", color: "#fff" } : { background: "#e2eaf2", color: "#9fb8cc" }}
          >
            {step === 4 ? "Concluir registo" : "Avançar"}
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>

        <p className="text-center text-muted text-xs mt-6">
          Já tem conta?{" "}
          <Link to="/access-user" className="text-blue font-semibold hover:text-blue-light transition-colors">
            Entrar aqui
          </Link>
        </p>
      </div>
    </div>
  );
}
